<script setup>
/**
 * "Am I about to run out of credit?" — deliberately NOT the same weight as the tiles
 * beside it.
 *
 * A blind critic, comparing this screen against Chatwoot Captain without knowing whose
 * was whose, named the exact defect this component exists to fix: *"The two cards carry
 * identical visual weight even though 'are replies on?' and 'am I about to run out of
 * credit?' are not equally urgent."* They were two `CardLayout`s with the same heading
 * size and the same everything else.
 *
 * So weight here is DATA-DRIVEN, not decorative. At `level: ok` this is the third tile
 * in the row and reads as quietly as the two counters. The moment the BFF says `low` or
 * `critical`, the number takes the warning colour, a banner appears under it, and the page
 * gives the card the full row (`sm:col-span-3`, applied by the parent through class
 * fall-through). Nothing about the urgency is a judgement this component makes: `level` and
 * `lowBalance` are computed once on the BFF, by DAYS LEFT at this pace when there is one
 * (P-5.3: amber at 10 days, red at 3 or at zero) and on the operator ladder otherwise.
 *
 * AND IT IS THE ONLY SURFACE ON THE PAGE THAT SPEAKS ABOUT THE BALANCE. The attention queue
 * lost the balance rather than this card, because this is where the number, the expiry
 * date, the pace and the one action live.
 *
 * P-5.3 ADDED TWO THINGS. The pace: "Used today", "Daily average over the last week" and "Lasts
 * about N days at this pace", counted in the account's own day (Kuwait midnight, not UTC).
 * And the one action, "Request top-up" (`TopUpRequestButton`), which replaced "Contact us to
 * top up": billing is manual, so the button asks us, and says "We've already been told"
 * when a low-balance alert or an earlier press already did.
 * Buy credits (2026-10-08) sits beside it and opens the buy page; paying is self-serve now.
 */
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import TopUpRequestButton from './TopUpRequestButton.vue';
import { useWasloI18n } from '../i18n.js';
import { toIntlLocale } from '../helpers/intl.js';
import { creditsPhrase } from '../helpers/ai-price.js';

const props = defineProps({
  /** The narrowed `{ credits }` payload, or null when the read failed. */
  credits: { type: Object, default: null },
  /** For "Request top-up". Null: the button is not drawn. */
  accountId: { type: Number, default: null },
});

const { t, locale } = useWasloI18n();

const intlLocale = computed(() => toIntlLocale(locale.value));

const formattedBalance = computed(() =>
  props.credits
    ? new Intl.NumberFormat(intlLocale.value).format(props.credits.balance)
    : ''
);

const nextExpiry = computed(() => {
  const raw = props.credits?.nextExpiry;
  if (!raw) return '';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(parsed);
});

// An internal workspace bills nobody. It still shows a balance — hiding it would make
// our own accounts the only ones we cannot debug — but it is never nagged to top up.
const isInternal = computed(() => props.credits?.internal === true);

const warning = computed(() => {
  const summary = props.credits;
  if (!summary || !summary.lowBalance) return '';
  if (summary.balance <= 0) return t('WASLO.OVERVIEW.CREDITS.EMPTY');
  if (summary.level === 'critical') return t('WASLO.OVERVIEW.CREDITS.CRITICAL');
  return t('WASLO.OVERVIEW.CREDITS.LOW');
});

/** Urgency, straight off the BFF's verdict. `ok` deliberately looks like a counter. */
const numberTone = computed(() => {
  if (!props.credits || !props.credits.lowBalance) return 'text-n-slate-12';
  return props.credits.level === 'critical'
    ? 'text-n-ruby-11'
    : 'text-n-amber-11';
});

const bannerColour = computed(() =>
  props.credits?.level === 'critical' ? 'ruby' : 'amber'
);

const subLine = computed(() => {
  if (!props.credits) return t('WASLO.OVERVIEW.CREDITS.UNAVAILABLE');
  if (isInternal.value) return t('WASLO.OVERVIEW.CREDITS.INTERNAL');
  return nextExpiry.value
    ? t('WASLO.OVERVIEW.CREDITS.NEXT_EXPIRY', { date: nextExpiry.value })
    : t('WASLO.OVERVIEW.CREDITS.NO_EXPIRY');
});

/**
 * P-5.3's three lines. A BFF one version behind sends no runway, and then there are none.
 * At an empty balance the "lasts" line is left out: the status line and the banner already
 * say the assistant stopped.
 */
const runwayLines = computed(() => {
  const summary = props.credits;
  const runway = summary?.runway;
  if (!summary || !runway) return [];
  const lines = [
    {
      key: 'today',
      text: t('WASLO.OVERVIEW.CREDITS.USED_TODAY', {
        credits: creditsPhrase(t, locale.value, runway.usedToday),
      }),
    },
    {
      key: 'average',
      text: t('WASLO.OVERVIEW.CREDITS.DAILY_AVERAGE', {
        credits: creditsPhrase(t, locale.value, runway.dailyAverage7d),
      }),
    },
  ];
  if (summary.balance <= 0) return lines;
  if (runway.daysLeft === null) {
    lines.push({
      key: 'lasts',
      text: t('WASLO.OVERVIEW.CREDITS.NOT_ENOUGH_USE'),
    });
  } else if (runway.daysLeft === 0) {
    lines.push({ key: 'lasts', text: t('WASLO.OVERVIEW.CREDITS.LASTS_TODAY') });
  } else {
    lines.push({
      key: 'lasts',
      text: t(
        'WASLO.OVERVIEW.CREDITS.LASTS',
        {
          count: new Intl.NumberFormat(intlLocale.value).format(
            runway.daysLeft
          ),
        },
        runway.daysLeft
      ),
    });
  }
  return lines;
});

const canRequest = computed(
  () => Boolean(props.credits) && !isInternal.value && props.accountId !== null
);

const router = useRouter();
// Buy credits (credit top-ups plan). Beside "Request top-up", never instead of it: the buy
// page itself falls back to asking us when paying online is not open on this environment.
const openBuy = () =>
  router.push({
    name: 'waslo_credits_buy',
    params: { accountId: props.accountId },
  });
</script>

<template>
  <CardLayout data-waslo-credits-card>
    <div class="flex flex-col gap-1">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WASLO.OVERVIEW.CREDITS.HEADING') }}
      </p>
      <div class="flex items-baseline gap-2">
        <span
          class="text-3xl font-medium leading-9"
          :class="numberTone"
          data-waslo-credit-balance
        >
          {{ formattedBalance }}
        </span>
        <span v-if="credits" class="text-sm text-n-slate-11">
          {{ t('WASLO.OVERVIEW.CREDITS.UNIT') }}
        </span>
      </div>
      <p class="mb-0 text-sm text-n-slate-10">{{ subLine }}</p>
    </div>
    <ul
      v-if="runwayLines.length"
      class="flex flex-col gap-0.5 mb-0 list-none ps-0"
      data-waslo-credit-runway
    >
      <li
        v-for="line in runwayLines"
        :key="line.key"
        class="text-sm text-n-slate-11"
        :data-waslo-credit-runway-line="line.key"
      >
        {{ line.text }}
      </li>
    </ul>
    <Banner v-if="warning" :color="bannerColour" data-waslo-credit-warning>
      {{ warning }}
    </Banner>
    <div v-if="canRequest" class="flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        :label="t('WASLO.BUY_CREDITS.OPEN')"
        data-waslo-buy-credits
        @click="openBuy"
      />
      <TopUpRequestButton
        :account-id="accountId"
        :already-told="credits.alreadyTold === true"
      />
    </div>
  </CardLayout>
</template>
